import * as Clipboard from "expo-clipboard";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { StyleSheet, View } from "react-native";
import { useTheme } from "react-native-elements";
import { hasEverTriggeredEnding } from "../backend/endings/Endings";
import {
  BOON_LEVEL_THAT_UNLOCKS_MASTERIES,
  getMaxPossibleBuffAndDifficultyLevel,
  getNewGamePlusAccuracyMultiplier,
  getNewGamePlusAttackDelayMultiplier,
  getNewGamePlusCostScaleMultiplier,
  getNewGamePlusDodgeStatMultiplier,
  getNewGamePlusHpMultiplier,
  getNewGamePlusOtherStatMultiplier,
  getNewGamePlusProductionMultiplier,
  getNewGamePlusResearchMultiplier,
  getNewGamePlusSchoolLevelBonus,
  getNewGamePlusStorageMultiplier,
  MAX_BUFF_DIFFICULTY_LEVEL,
  triggerNewGamePlus,
} from "../backend/NewGamePlus";
import { PlayerContext } from "../backend/PlayerContext";
import { serializeSaveData } from "../backend/SaveData";
import { SCHOOL_LEVEL_CAP_BASE } from "../backend/spells/ElementsAndSchools";
import { saveToFile, SAVE_TO_FILE_SUPPORTED } from "../utils/FilesystemSave";
import { formatNumber, RoundingMethod } from "../utils/FormattingUtils";
import { Button } from "./utility/Button";
import { Markdown } from "./utility/Markdown";
import { PlusMinusAllocator } from "./utility/PlusMinusAllocator";
import { Text } from "./utility/Text";
import { VisibilityContext } from "./utility/VisibilityContext";

export default function Creation() {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const theme = useTheme();

  const maxLevel = getMaxPossibleBuffAndDifficultyLevel(playerContext);
  const [boonLevel, setBoonLevel] = useState(maxLevel);
  const boonLevelRef = useRef(boonLevel);
  boonLevelRef.current = boonLevel;
  const [difficultyLevel, setDifficultyLevel] = useState(maxLevel);
  const difficultyLevelRef = useRef(difficultyLevel);
  difficultyLevelRef.current = difficultyLevel;

  const incrementBoonLevel = useCallback(() => {
    setBoonLevel(Math.min(boonLevelRef.current + 1, maxLevel));
  }, [maxLevel, setBoonLevel]);
  const canIncrementBoonLevel = boonLevel + 1 <= maxLevel;
  const decrementBoonLevel = useCallback(() => {
    setBoonLevel(Math.max(boonLevelRef.current - 1, 0));
  }, [setBoonLevel]);
  const canDecrementBoonLevel = boonLevel - 1 >= 0;

  const incrementDifficultyLevel = useCallback(() => {
    setDifficultyLevel(Math.min(difficultyLevelRef.current + 1, maxLevel));
  }, [maxLevel, setDifficultyLevel]);
  const canIncrementDifficultyLevel = difficultyLevel + 1 <= maxLevel;
  const decrementDifficultyLevel = useCallback(() => {
    setDifficultyLevel(Math.max(difficultyLevelRef.current - 1, 0));
  }, [setDifficultyLevel]);
  const canDecrementDifficultyLevel = difficultyLevel - 1 >= 0;

  // Current world

  const currentBoonLevel = playerContext.global.newGamePlusBuffLevel;
  const researchPctCurrent = formatNumber(
    100 * (getNewGamePlusResearchMultiplier(currentBoonLevel) - 1),
    { showDecimals: true, roundingMethod: RoundingMethod.UP },
  );
  const productionPctCurrent = formatNumber(
    100 * (getNewGamePlusProductionMultiplier(currentBoonLevel) - 1),
    { showDecimals: true },
  );
  const storagePctCurrent = formatNumber(
    100 * (getNewGamePlusStorageMultiplier(currentBoonLevel) - 1),
    { showDecimals: true, roundingMethod: RoundingMethod.UP },
  );
  const costScalePctCurrent = formatNumber(
    -100 * (getNewGamePlusCostScaleMultiplier(currentBoonLevel) - 1),
    { showDecimals: true },
  );
  const maxSchoolLevelCurrent = formatNumber(
    SCHOOL_LEVEL_CAP_BASE + getNewGamePlusSchoolLevelBonus(currentBoonLevel),
  );

  const masteryUnlocked =
    currentBoonLevel >= BOON_LEVEL_THAT_UNLOCKS_MASTERIES
      ? " You also begin with all school mastery Storylines completed."
      : "";

  const currentBoonText =
    currentBoonLevel > 0
      ? `
  
The current world has a boon level of **${currentBoonLevel}**, which is giving the following boons: **+${researchPctCurrent}% to all Research, +${productionPctCurrent}% to all Production and Loot, +${storagePctCurrent}% to all resource maximums (except :mana: and :timepiece:), building costs scale ${costScalePctCurrent}% slower, and the maximum School level is ${maxSchoolLevelCurrent}.${masteryUnlocked}**`
      : "";

  const currentDifficultyLevel =
    playerContext.global.newGamePlusDifficultyLevel;
  const hpAtkPctCurrent = formatNumber(
    100 * (getNewGamePlusHpMultiplier(currentDifficultyLevel) - 1),
    { showDecimals: true, roundingMethod: RoundingMethod.UP },
  );

  const statPctCurrent = formatNumber(
    100 * (getNewGamePlusOtherStatMultiplier(currentDifficultyLevel) - 1),
    { showDecimals: true, roundingMethod: RoundingMethod.UP },
  );

  const dodgePctCurrent = formatNumber(
    100 * (getNewGamePlusDodgeStatMultiplier(currentDifficultyLevel) - 1),
    { showDecimals: true },
  );

  const accuracyPctCurrent = formatNumber(
    100 * (getNewGamePlusAccuracyMultiplier(currentDifficultyLevel) - 1),
    { showDecimals: true },
  );

  const attackDelayPctCurrent = formatNumber(
    -100 * (getNewGamePlusAttackDelayMultiplier(currentDifficultyLevel) - 1),
    { showDecimals: true },
  );

  const currentDifficultyText =
    currentDifficultyLevel > 0
      ? `
  
The current world has a difficulty level of **${currentDifficultyLevel}**, which is giving the following extra difficulty: **+${hpAtkPctCurrent}% enemy :hp:; +${statPctCurrent}% enemy :attack:, :defense:; +${dodgePctCurrent}% enemy :dodge:; +${accuracyPctCurrent}% enemy :accuracy:; -${attackDelayPctCurrent}% enemy :attackDelay:**`
      : "";

  const modifiedWorldText =
    currentDifficultyLevel > 0 || currentBoonLevel > 0
      ? `You are currently playing on a **modified world**.${currentBoonText}${currentDifficultyText}`
      : null;

  const currentComponent =
    modifiedWorldText != null ? <Markdown>{modifiedWorldText}</Markdown> : null;

  // New world

  const researchPct = formatNumber(
    100 * (getNewGamePlusResearchMultiplier(boonLevel) - 1),
    { showDecimals: true },
  );
  const productionPct = formatNumber(
    100 * (getNewGamePlusProductionMultiplier(boonLevel) - 1),
    { showDecimals: true },
  );
  const storagePct = formatNumber(
    100 * (getNewGamePlusStorageMultiplier(boonLevel) - 1),
    { showDecimals: true },
  );
  const costScalePct = formatNumber(
    -100 * (getNewGamePlusCostScaleMultiplier(boonLevel) - 1),
    { showDecimals: true },
  );
  const maxSchoolLevel = formatNumber(
    SCHOOL_LEVEL_CAP_BASE + getNewGamePlusSchoolLevelBonus(boonLevel),
  );

  const masteryUnlockedNew =
    boonLevel >= BOON_LEVEL_THAT_UNLOCKS_MASTERIES
      ? " You will also begin with all school mastery Storylines completed."
      : "";

  const boonText =
    boonLevel > 0
      ? `The new world will have the following boons: **+${researchPct}% to all Research, +${productionPct}% to all Production and Loot, +${storagePct}% to all resource maximums (except :mana: and :timepiece:), building costs scale ${costScalePct}% slower, and the maximum School level will become ${maxSchoolLevel}.${masteryUnlockedNew}**`
      : `The new world will **not have any boons!**`;

  const hpAtkPct = formatNumber(
    100 * (getNewGamePlusHpMultiplier(difficultyLevel) - 1),
    { showDecimals: true, roundingMethod: RoundingMethod.UP },
  );

  const statPct = formatNumber(
    100 * (getNewGamePlusOtherStatMultiplier(difficultyLevel) - 1),
    { showDecimals: true, roundingMethod: RoundingMethod.UP },
  );

  const dodgePct = formatNumber(
    100 * (getNewGamePlusDodgeStatMultiplier(difficultyLevel) - 1),
    { showDecimals: true },
  );

  const accuracyPct = formatNumber(
    100 * (getNewGamePlusAccuracyMultiplier(difficultyLevel) - 1),
    { showDecimals: true },
  );

  const attackDelayPct = formatNumber(
    -100 * (getNewGamePlusAttackDelayMultiplier(difficultyLevel) - 1),
    { showDecimals: true },
  );

  const difficultyText =
    difficultyLevel > 0
      ? `The new world will have the following extra difficulty: **+${hpAtkPct}% enemy :hp:; +${statPct}% enemy :attack:, :defense:; +${dodgePct}% enemy :dodge:; +${accuracyPct}% enemy :accuracy:; -${attackDelayPct}% enemy :attackDelay:**`
      : `The new world will **not have any extra difficulty!**`;

  const text = `You are ready to cast the Spell of Creation! However, you feel like you might not have had enough of this world. You contemplate using the Spell of Creation to bring into existence a new world, but at the same time, you think it might be best if things were just a *little* different...

By creating a new world, **you will start over the game from the very beginning, and will forfeit everything:**

* Unlocked schools, elements, and Storylines will **not** carry over;
* Your MPL values, as well as your global Research and Production multipliers, will **not** carry over.

The Creation screen will persist through new worlds - you will not need to unlock it again.`;

  const noContentWarning = (
    <Text style={[styles.error, { color: theme.theme.colors.error }]}>
      Using the Spell of Creation does not unlock any new content! It is only
      meant as a way to experience the game again.
    </Text>
  );

  const levelWarning = (
    <Text style={[styles.error, { color: theme.theme.colors.warning }]}>
      The new world's difficulty level is higher than the boon level. Are you
      sure you want to do this?
    </Text>
  );
  const tooDifficultWarning = (
    <Text style={[styles.error, { color: theme.theme.colors.warning }]}>
      The new world's difficulty level is very high! Are you sure you want to do
      this?
    </Text>
  );

  const boonComponent = useMemo(
    () => (
      <View style={styles.boonDiffBigContainer}>
        <View style={styles.row}>
          <View style={styles.boonDiffContainer}>
            <Text>New world's boon level:</Text>
          </View>
          <View style={[styles.row, styles.rightHandSide]}>
            <Text>{boonLevel}</Text>
            <View style={styles.plusMinusAllocatorContainer}>
              <PlusMinusAllocator
                disableMinus={!canDecrementBoonLevel}
                disablePlus={!canIncrementBoonLevel}
                onPressMinus={decrementBoonLevel}
                onPressPlus={incrementBoonLevel}
              />
            </View>
          </View>
        </View>
        <View style={styles.boonDiffExplanation}>
          <Markdown>{boonText}</Markdown>
        </View>
      </View>
    ),
    [
      boonLevel,
      boonText,
      canDecrementBoonLevel,
      canIncrementBoonLevel,
      decrementBoonLevel,
      incrementBoonLevel,
    ],
  );
  const difficultyComponent = useMemo(
    () => (
      <View style={styles.boonDiffBigContainer}>
        <View style={styles.row}>
          <View style={styles.boonDiffContainer}>
            <Text>New world's difficulty level:</Text>
          </View>
          <View style={[styles.row, styles.rightHandSide]}>
            <Text>{difficultyLevel}</Text>
            <View style={styles.plusMinusAllocatorContainer}>
              <PlusMinusAllocator
                disableMinus={!canDecrementDifficultyLevel}
                disablePlus={!canIncrementDifficultyLevel}
                onPressMinus={decrementDifficultyLevel}
                onPressPlus={incrementDifficultyLevel}
              />
            </View>
          </View>
        </View>
        <View style={styles.boonDiffExplanation}>
          <Markdown>{difficultyText}</Markdown>
        </View>
      </View>
    ),
    [
      difficultyLevel,
      difficultyText,
      canDecrementDifficultyLevel,
      canIncrementDifficultyLevel,
      decrementDifficultyLevel,
      incrementDifficultyLevel,
    ],
  );

  const maximumText = `You may change the boon and difficulty level of the new world to a maximum of **${formatNumber(
    maxLevel,
  )}**.${
    maxLevel < MAX_BUFF_DIFFICULTY_LEVEL
      ? ` To increase this number, you must win the Tournament of Magic${
          !hasEverTriggeredEnding(playerContext, "standard_ending")
            ? ""
            : " on a new world"
        }.`
      : ""
  }`;

  const [hasExportedGame, setHasExportedGame] = useState(false);
  const visible = useContext(VisibilityContext);
  useEffect(() => {
    if (!visible) {
      setHasExportedGame(false);
    }
  }, [visible]);

  const copyToClipboard = useCallback(async () => {
    const serializedData = await serializeSaveData(playerContextRef.current);
    await Clipboard.setStringAsync(serializedData);
    setHasExportedGame(true);
  }, [setHasExportedGame]);

  const saveToFileCallback = useCallback(async () => {
    const result = await saveToFile(playerContextRef.current);
    setHasExportedGame(true);
  }, [setHasExportedGame]);

  const saveFileCategory = useMemo(
    () => (
      <View style={styles.saveFileCategory}>
        <View style={styles.saveFileExplanation}>
          <Text>
            Since creating a new world is irreversible, you will need to export
            your save data first before being able to cast the Spell of
            Creation.
          </Text>
        </View>
        <Button
          onPress={copyToClipboard}
          title="Copy savefile to clipboard"
          containerStyle={styles.saveButton}
        />
        {SAVE_TO_FILE_SUPPORTED && (
          <Button
            onPress={saveToFileCallback}
            title="Save to file"
            containerStyle={styles.saveButton}
          />
        )}
      </View>
    ),
    [copyToClipboard, SAVE_TO_FILE_SUPPORTED, saveToFileCallback],
  );

  return (
    <View style={styles.container}>
      <Text h1>Creation</Text>
      <View style={styles.explanation}>
        <Markdown>{text}</Markdown>
        <View style={styles.warning}>{noContentWarning}</View>
      </View>
      {currentComponent != null && (
        <View style={styles.worldSettings}>{currentComponent}</View>
      )}
      <View style={styles.worldSettings}>
        <Markdown>{maximumText}</Markdown>
        {boonComponent}
        {difficultyComponent}
      </View>
      {difficultyLevel > boonLevel && levelWarning}
      {difficultyLevel > 30 && tooDifficultWarning}
      {saveFileCategory}
      <View style={styles.buttonContainer}>
        {
          <Button
            title="Cast the Spell of Creation"
            color="error"
            onPress={() => {
              playerContextRef.current.apply((state) =>
                triggerNewGamePlus(state, boonLevel, difficultyLevel),
              );
            }}
            disabled={!hasExportedGame}
          />
        }
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
  },
  explanation: {
    marginTop: 16,
  },
  buttonContainer: {
    marginTop: 16,
  },
  warning: {
    marginTop: 8,
  },
  error: {
    fontWeight: "bold",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  worldSettings: {
    marginTop: 16,
  },
  saveFileCategory: {
    marginTop: 16,
  },
  rightHandSide: {
    marginLeft: 16,
  },
  plusMinusAllocatorContainer: {
    marginLeft: 16,
  },
  boonDiffBigContainer: {
    marginTop: 8,
  },
  boonDiffExplanation: {
    marginTop: 8,
  },
  boonDiffContainer: {
    flex: 1,
  },
  saveButton: {
    marginTop: 8,
  },
  saveFileExplanation: {
    marginBottom: 8,
  },
});
